import { motion } from 'framer-motion';
import React from 'react';

import avatar from '../../assets/images/Muser.jpg';

type MicroStepsProps = {
  imageUrl: string;
  name: string;
  role: string;
  salaryTarget: string;
  requiredSkills: string[];
  microSteps: string[];
};

export const MicroSteps: React.FC<MicroStepsProps> = ({
  imageUrl,
  name,
  role,
  salaryTarget,
  requiredSkills,
  microSteps,
}) => {
  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-2xl mx-auto border border-gray-100"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      {/* Header */}
      <div>
        <h3 className="text-center text-2xl font-bold text-gray-900">Training to Advance</h3>
        <hr className="my-6 border-gray-200" />

        {/* Profile */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            <img
              src={imageUrl || avatar}
              alt="User avatar"
              className="w-16 h-16 rounded-full object-cover"
            />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{name}</h2>
              <p className="text-sm text-gray-800 font-semibold">{role || 'Student'}</p>
            </div>
          </div>
        </div>

        <hr className="my-6 border-gray-200 rounded-full" />

        {/* Salary target */}
        <div className="text-center md:text-left mb-6">
          <p className="text-gray-600 text-sm font-medium mb-1">Salary Target</p>
          <p className="text-xl font-bold text-green-700">{salaryTarget}</p>
        </div>

        <hr className="my-6 border-gray-200" />

        {/* Micro Steps */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Micro Steps</h3>
          <div className="flex flex-col space-y-3">
            {microSteps && microSteps.length > 0 ? (
              microSteps.map((step, idx) => (
                <motion.div
                  key={idx}
                  className="flex items-start gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, duration: 0.4 }}
                  whileHover={{ scale: 1.06 }}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-6 h-6 flex items-center justify-center bg-green-500 text-white font-semibold rounded-full text-xs">
                      {idx + 1}
                    </div>
                    {idx < microSteps.length - 1 && (
                      <div className="w-[2px] h-6 bg-green-300"></div>
                    )}
                  </div>
                  <span className="bg-green-100 text-green-800 px-4 py-1 rounded-xl text-sm font-medium">
                    {step}
                  </span>
                </motion.div>
              ))
            ) : (
              <p className="text-gray-400 italic">No micro steps available.</p>
            )}
          </div>
        </div>

        <hr className="my-6 border-gray-200" />

        {/* Required Skills */}
        <div>
          <h3 className="font-semibold text-gray-900 mb-3">Required Skills</h3>
          <div className="flex flex-wrap gap-2">
            {requiredSkills && requiredSkills.length > 0 ? (
              requiredSkills.map((skill, idx) => {
                const formattedSkill = skill.charAt(0).toUpperCase() + skill.slice(1).toLowerCase();

                return (
                  <motion.div
                    key={idx}
                    className="bg-indigo-50 text-indigo-700 px-4 py-1 rounded-full text-sm font-medium"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.15, duration: 0.4 }}
                    whileHover={{ scale: 1.06 }}
                  >
                    {formattedSkill}
                  </motion.div>
                );
              })
            ) : (
              <p className="text-gray-400 italic">No skills required.</p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
